import React from 'react';
import { useNavigate } from 'react-router-dom';
import { storage } from '../utils/platform';

/**
 * MobileLanding — premier écran, avant connexion.
 *
 * Reprend l'identité de la landing web : fond noir profond (#0A0908) et halos
 * orange en dégradés radiaux, au lieu du beige de l'app connectée. C'est le
 * premier contact avec la marque, il doit ressembler au site.
 *
 * ⚠️ Le halo central n'est pas décoratif : le slogan comporte un mot en NOIR,
 * illisible sur fond noir sans lui. Il est placé derrière la deuxième ligne
 * pour que « plus de » s'y détache. Déplacer l'un sans l'autre casse la
 * lisibilité du slogan.
 *
 * ⚠️ Aucun bouton menant à un achat ou à une page tarifaire (App Store
 * 3.1.3(f), cf. utils/storeCompliance.js) : uniquement se connecter et créer un
 * compte. Le bouton « Calculer mon score RPS », qui ouvrait le site, a été
 * retiré.
 */
export default function MobileLanding() {
  const navigate = useNavigate();

  const markSeen = async () => {
    try { await storage.set('revyo_onboarding_seen', 'true'); } catch (_) {}
  };

  const go = async (to) => {
    if ('vibrate' in navigator) navigator.vibrate(5);
    await markSeen();
    navigate(to);
  };

  return (
    <div style={styles.page}>
      {/* Halos : le premier éclaire le slogan, les autres habillent les angles. */}
      <div style={{ ...styles.glow, ...styles.glowCenter }} aria-hidden />
      <div style={{ ...styles.glow, ...styles.glowTop }} aria-hidden />
      <div style={{ ...styles.glow, ...styles.glowBottom }} aria-hidden />

      <div style={styles.center}>
        <div style={styles.logoSquare} aria-hidden>R</div>
        <div style={styles.brand}>Revyo</div>

        <h1 style={styles.hook}>
          <span style={styles.hookWhite}>Plus d'avis,</span>
          <br />
          <span style={styles.hookBlack}>plus de </span>
          <span style={styles.hookWhite}>clients</span>
        </h1>
      </div>

      <div style={styles.actions}>
        <button type="button" onClick={() => go('/login')} style={styles.btnPrimary}>
          Se connecter
        </button>
        <button type="button" onClick={() => go('/register')} style={styles.btnOutlined}>
          Créer un compte
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: {
    position: 'relative',
    minHeight: '100dvh',
    background: '#0A0908',
    color: '#F5F2EB',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    paddingTop: 'calc(env(safe-area-inset-top) + 56px)',
    paddingBottom: 'calc(env(safe-area-inset-bottom) + 28px)',
    paddingLeft: 24,
    paddingRight: 24,
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  glow: {
    position: 'absolute',
    borderRadius: '50%',
    pointerEvents: 'none',
  },
  // Halo central — c'est lui qui rend lisible le mot noir du slogan.
  glowCenter: {
    width: 460,
    height: 460,
    top: '32%',
    left: '50%',
    transform: 'translate(-50%, -30%)',
    background:
      'radial-gradient(circle, rgba(232,80,42,0.58) 0%, rgba(199,62,29,0.24) 45%, transparent 70%)',
  },
  glowTop: {
    width: 320,
    height: 320,
    top: -110,
    right: -90,
    background: 'radial-gradient(circle, rgba(255,138,61,0.30) 0%, transparent 65%)',
  },
  glowBottom: {
    width: 300,
    height: 300,
    bottom: -120,
    left: -80,
    background: 'radial-gradient(circle, rgba(199,62,29,0.28) 0%, transparent 65%)',
  },
  center: {
    position: 'relative',
    zIndex: 1,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  logoSquare: {
    width: 76,
    height: 76,
    borderRadius: 20,
    background: '#E8502A',
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 42,
    fontWeight: 800,
    lineHeight: 1,
    letterSpacing: '-0.02em',
    boxShadow: '0 14px 40px rgba(232,80,42,0.42)',
  },
  brand: {
    marginTop: 14,
    fontSize: 26,
    fontWeight: 800,
    letterSpacing: '-0.02em',
    color: '#FFFFFF',
  },
  hook: {
    margin: '38px 0 0',
    fontSize: 38,
    lineHeight: 1.15,
    fontWeight: 800,
    letterSpacing: '-0.03em',
  },
  hookWhite: { color: '#FFFFFF' },
  // Noir profond, posé sur le halo orange — d'où sa lisibilité.
  hookBlack: { color: '#0A0908' },
  actions: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  btnPrimary: {
    width: '100%',
    padding: '16px 20px',
    borderRadius: 999,
    border: 'none',
    background: '#E8502A',
    color: '#FFFFFF',
    fontFamily: 'inherit',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
  },
  btnOutlined: {
    width: '100%',
    padding: '16px 20px',
    borderRadius: 999,
    border: '1.5px solid rgba(255,255,255,0.30)',
    background: 'transparent',
    color: '#FFFFFF',
    fontFamily: 'inherit',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
  },
};
